/**
 * RibbonScene — the public site's background ribbon.
 *
 * Mounted ONCE in the marketing layout and re-configured per route: a
 * navigation swaps the scene (preset, opacity, waypoints) without rebuilding the
 * WebGL context, so a visitor moving between pages sees one ribbon change
 * shape rather than a white flash and a new canvas.
 *
 * The engine is driven from GSAP's ticker — the same clock SmoothScroll gives
 * Lenis and ScrollTrigger — so the ribbon never drifts behind the smoothed
 * scroll position. It reads `window.scrollY`, which Lenis keeps in step.
 *
 * `prefers-reduced-motion` puts the engine in its reduced mode: no idle drift
 * and no pointer lens, while the scroll-linked shape still follows the page.
 * Re-evaluated on change, like SmoothScroll.
 *
 * The two `data-ribbon-*` attributes below are the VISITOR-facing test hooks,
 * and live here on purpose — a CMS surface never renders this component, so an
 * acceptance assertion against them can only be satisfied by the public site.
 */
import { useEffect, useMemo, useState } from "react";
import { gsap } from "gsap";
import {
  RibbonBackground,
  buildRibbonScene,
  windowScrollSource,
  type RibbonDriver,
  type RibbonScene as RibbonSceneConfig,
} from "@diality/ui";
import type { PageRibbon } from "../marketing/page-loader";

const gsapTickerDriver: RibbonDriver = {
  subscribe(tick) {
    const fn = () => tick(performance.now());
    gsap.ticker.add(fn);
    return () => gsap.ticker.remove(fn);
  },
};

export function RibbonScene({
  ribbon,
  pathname,
}: {
  ribbon: PageRibbon | null;
  pathname: string;
}) {
  const [reducedMotion, setReducedMotion] = useState(false);
  const [stateId, setStateId] = useState<string | null>(null);
  const [position, setPosition] = useState<"pinned" | "released" | "offscreen">("pinned");

  useEffect(() => {
    const query = window.matchMedia("(prefers-reduced-motion: reduce)");
    const sync = () => setReducedMotion(query.matches);
    sync();
    query.addEventListener("change", sync);
    return () => query.removeEventListener("change", sync);
  }, []);

  const scene = useMemo<RibbonSceneConfig | null>(
    () => (ribbon ? buildRibbonScene(ribbon) : null),
    [ribbon],
  );

  return (
    <div
      className="contents"
      data-ribbon-scene={ribbon?.opening ?? undefined}
      data-ribbon-state={ribbon ? (stateId ?? undefined) : undefined}
      data-ribbon-position={ribbon ? position : undefined}
    >
      <RibbonBackground
        scene={scene}
        mode={reducedMotion ? "reduced" : "live"}
        driver={gsapTickerDriver}
        scroll={windowScrollSource}
        // A new page is a new set of block positions; the layout scrolls to the
        // top on the same commit, so measuring on the next frame is correct.
        measureKey={pathname}
        onState={(state) => {
          setStateId(state.stateId);
          setPosition(state.position);
        }}
      />
    </div>
  );
}
